import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      courses: 'Courses',
      analysis: 'Analysis',
      testimonials: 'Testimonials',
      dashboard: 'Dashboard',
      signIn: 'Sign In',
      getStarted: 'Get Started',
      backToHome: 'Back to Home',
      logout: 'Sign Out',
    },
    // Landing Page
    landing: {
      heroTag: 'Live Trading Education',
      heroTitle: 'Master The',
      heroTitleHighlight: 'Markets',
      heroDescription: 'Professional trading education from industry experts. Learn stocks, options, and futures with real-world strategies that deliver results.',
      startLearning: 'Start Learning',
      viewCourses: 'View Courses',
      activeStudents: 'Active Students',
      successRate: 'Success Rate',
      studentProfits: 'Student Profits',
      premiumCourses: 'Premium Courses',
      premiumCoursesDesc: 'Comprehensive trading education designed by professionals, for professionals.',
      mostPopular: 'Most Popular',
      enrollNow: 'Enroll Now',
      liveSignals: 'Live Signals',
      liveSignalsTitle: 'Live Trading Signals',
      liveSignalsDesc: 'Get real-time trading signals from professional traders. Daily alerts delivered to your dashboard.',
      perWeek: '/week',
      subscribeNow: 'Subscribe Now',
      testimonialTitle: 'Student Success Stories',
      testimonialSubtitle: 'Real results from real traders',
      newsletterTitle: 'Weekly Market Insights',
      newsletterDesc: 'Get exclusive market analysis and trading tips delivered to your inbox every week.',
      subscribe: 'Subscribe',
      subscribed: 'Successfully subscribed!',
      subscribeFailed: 'Failed to subscribe. Please try again.',
    },
    // Auth
    auth: {
      welcomeBack: 'Welcome Back',
      signInDesc: 'Sign in to access your dashboard',
      email: 'Email',
      password: 'Password',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      createAccount: 'Create Account',
      createAccountDesc: 'Start your trading journey today',
      fullName: 'Full Name',
      confirmPassword: 'Confirm Password',
      alreadyHaveAccount: 'Already have an account?',
      creatingAccount: 'Creating Account...',
      signingIn: 'Signing in...',
    },
    // Dashboard
    dashboard: {
      welcomeBack: 'Welcome back',
      overview: 'Overview',
      myCourses: 'My Courses',
      watchlist: 'Watchlist',
      resources: 'Resources',
      bookConsulting: 'Book Consulting',
      profile: 'Profile',
      adminPanel: 'Admin Panel',
      enrolledCourses: 'Enrolled Courses',
      totalLessons: 'Total Lessons',
      consultingSessions: 'Consulting Sessions',
      accountStatus: 'Account Status',
      yourCourses: 'Your Courses',
      viewAll: 'View All',
      continue: 'Continue',
      browseCourses: 'Browse Courses',
      upcomingSessions: 'Upcoming Sessions',
      bookSession: 'Book Session',
      noCoursesYet: 'No Courses Yet',
      noCoursesDesc: "You haven't enrolled in any courses yet. Start your trading journey today!",
      noUpcomingSessions: 'No upcoming sessions',
      quickActions: 'Quick Actions',
    },
    // Course
    course: {
      lessons: 'lessons',
      viewCourse: 'View Course',
      backToCourses: 'Back to Courses',
      courseLessons: 'Course Lessons',
      watch: 'Watch',
      progress: 'Progress',
      completed: 'Completed',
      markComplete: 'Mark Complete',
      notes: 'Lesson Notes',
      downloadPdf: 'Download PDF',
    },
    // Legal
    legal: {
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      disclaimer: 'Disclaimer',
      contact: 'Contact',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      active: 'Active',
      admin: 'Admin',
      student: 'Student',
    },
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      courses: 'Cours',
      analysis: 'Analyse',
      testimonials: 'Témoignages',
      dashboard: 'Tableau de bord',
      signIn: 'Connexion',
      getStarted: 'Commencer',
      backToHome: "Retour à l'accueil",
      logout: 'Déconnexion',
    },
    // Landing Page
    landing: {
      heroTag: 'Formation Trading en Direct',
      heroTitle: 'Maîtrisez Les',
      heroTitleHighlight: 'Marchés',
      heroDescription: "Formation trading professionnelle par des experts de l'industrie. Apprenez les actions, options et futures avec des stratégies concrètes.",
      startLearning: "Commencer l'apprentissage",
      viewCourses: 'Voir les cours',
      activeStudents: 'Étudiants Actifs',
      successRate: 'Taux de Réussite',
      studentProfits: 'Profits des Étudiants',
      premiumCourses: 'Cours Premium',
      premiumCoursesDesc: 'Formation trading complète conçue par des professionnels, pour des professionnels.',
      mostPopular: 'Plus Populaire',
      enrollNow: "S'inscrire",
      liveSignals: 'Signaux en Direct',
      liveSignalsTitle: 'Signaux de Trading en Direct',
      liveSignalsDesc: 'Recevez des signaux de trading en temps réel. Alertes quotidiennes sur votre tableau de bord.',
      perWeek: '/semaine',
      subscribeNow: "S'abonner",
      testimonialTitle: 'Histoires de Réussite',
      testimonialSubtitle: 'Des résultats réels de vrais traders',
      newsletterTitle: 'Analyses Hebdomadaires',
      newsletterDesc: 'Recevez des analyses de marché exclusives chaque semaine.',
      subscribe: "S'abonner",
      subscribed: 'Inscription réussie!',
      subscribeFailed: "Échec de l'inscription. Réessayez.",
    },
    // Auth
    auth: {
      welcomeBack: 'Bon Retour',
      signInDesc: 'Connectez-vous pour accéder à votre tableau de bord',
      email: 'Email',
      password: 'Mot de passe',
      forgotPassword: 'Mot de passe oublié?',
      noAccount: "Pas encore de compte?",
      signUp: "S'inscrire",
      createAccount: 'Créer un Compte',
      createAccountDesc: 'Commencez votre parcours trading',
      fullName: 'Nom Complet',
      confirmPassword: 'Confirmer le Mot de Passe',
      alreadyHaveAccount: 'Déjà un compte?',
      creatingAccount: 'Création du compte...',
      signingIn: 'Connexion...',
    },
    // Dashboard
    dashboard: {
      welcomeBack: 'Bienvenue',
      overview: 'Aperçu',
      myCourses: 'Mes Cours',
      watchlist: 'Liste de Suivi',
      resources: 'Ressources',
      bookConsulting: 'Réserver Consultation',
      profile: 'Profil',
      adminPanel: "Panneau d'Admin",
      enrolledCourses: 'Cours Inscrits',
      totalLessons: 'Total des Leçons',
      consultingSessions: 'Sessions de Consultation',
      accountStatus: 'Statut du Compte',
      yourCourses: 'Vos Cours',
      viewAll: 'Voir Tout',
      continue: 'Continuer',
      browseCourses: 'Parcourir les Cours',
      upcomingSessions: 'Sessions à Venir',
      bookSession: 'Réserver une Session',
      noCoursesYet: 'Pas Encore de Cours',
      noCoursesDesc: "Vous n'êtes inscrit à aucun cours. Commencez votre parcours!",
      noUpcomingSessions: 'Pas de sessions à venir',
      quickActions: 'Actions Rapides',
    },
    // Course
    course: {
      lessons: 'leçons',
      viewCourse: 'Voir le Cours',
      backToCourses: 'Retour aux Cours',
      courseLessons: 'Leçons du Cours',
      watch: 'Regarder',
      progress: 'Progression',
      completed: 'Terminé',
      markComplete: 'Marquer Terminé',
      notes: 'Notes de Leçon',
      downloadPdf: 'Télécharger PDF',
    },
    // Legal
    legal: {
      terms: "Conditions d'Utilisation",
      privacy: 'Politique de Confidentialité',
      disclaimer: 'Avertissement',
      contact: 'Contact',
    },
    // Common
    common: {
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      add: 'Ajouter',
      search: 'Rechercher',
      filter: 'Filtrer',
      all: 'Tous',
      active: 'Actif',
      admin: 'Admin',
      student: 'Étudiant',
    },
  },
  ht: {
    // Navigation (Haitian Creole)
    nav: {
      home: 'Lakay',
      courses: 'Kou',
      analysis: 'Analiz',
      testimonials: 'Temwayaj',
      dashboard: 'Tablo',
      signIn: 'Konekte',
      getStarted: 'Kòmanse',
      backToHome: 'Retounen Lakay',
      logout: 'Dekonekte',
    },
    // Landing Page
    landing: {
      heroTag: 'Edikasyon Trading an Dirèk',
      heroTitle: 'Metrize',
      heroTitleHighlight: 'Mache yo',
      heroDescription: 'Edikasyon trading pwofesyonèl nan men ekspè. Aprann aksyon, opsyon ak fiti ak estrateji reyèl ki bay rezilta.',
      startLearning: 'Kòmanse Aprann',
      viewCourses: 'Gade Kou yo',
      activeStudents: 'Elèv Aktif',
      successRate: 'To Siksè',
      studentProfits: 'Pwofi Elèv',
      premiumCourses: 'Kou Premium',
      premiumCoursesDesc: 'Edikasyon trading konplè konsevwa pa pwofesyonèl, pou pwofesyonèl.',
      mostPopular: 'Pi Popilè',
      enrollNow: 'Enskri Kounye a',
      liveSignals: 'Siyal an Dirèk',
      liveSignalsTitle: 'Siyal Trading an Dirèk',
      liveSignalsDesc: 'Resevwa siyal trading an tan reyèl. Alèt chak jou nan tablo ou.',
      perWeek: '/semèn',
      subscribeNow: 'Abòne Kounye a',
      testimonialTitle: 'Istwa Siksè Elèv',
      testimonialSubtitle: 'Rezilta reyèl nan men vrè traders',
      newsletterTitle: 'Analiz Chak Semèn',
      newsletterDesc: 'Resevwa analiz mache eksklizif ak konsèy trading chak semèn.',
      subscribe: 'Abòne',
      subscribed: 'Abònman reyisi!',
      subscribeFailed: 'Echèk abònman. Eseye ankò.',
    },
    // Auth
    auth: {
      welcomeBack: 'Byenveni Ankò',
      signInDesc: 'Konekte pou aksede tablo ou',
      email: 'Imèl',
      password: 'Modpas',
      forgotPassword: 'Bliye modpas?',
      noAccount: "Pa gen kont?",
      signUp: 'Enskri',
      createAccount: 'Kreye Kont',
      createAccountDesc: 'Kòmanse vwayaj trading ou jodi a',
      fullName: 'Non Konplè',
      confirmPassword: 'Konfime Modpas',
      alreadyHaveAccount: 'Deja gen yon kont?',
      creatingAccount: 'Kreye kont...',
      signingIn: 'Konekte...',
    },
    // Dashboard
    dashboard: {
      welcomeBack: 'Byenveni',
      overview: 'Apèsi',
      myCourses: 'Kou Mwen yo',
      watchlist: 'Lis Siveyans',
      resources: 'Resous',
      bookConsulting: 'Rezève Konsiltasyon',
      profile: 'Pwofil',
      adminPanel: 'Pano Admin',
      enrolledCourses: 'Kou Enskri',
      totalLessons: 'Total Leson',
      consultingSessions: 'Sesyon Konsiltasyon',
      accountStatus: 'Estati Kont',
      yourCourses: 'Kou Ou yo',
      viewAll: 'Gade Tout',
      continue: 'Kontinye',
      browseCourses: 'Gade Kou yo',
      upcomingSessions: 'Sesyon k ap Vini',
      bookSession: 'Rezève Sesyon',
      noCoursesYet: 'Pa Gen Kou Ankò',
      noCoursesDesc: "Ou poko enskri nan okenn kou. Kòmanse vwayaj ou jodi a!",
      noUpcomingSessions: 'Pa gen sesyon k ap vini',
      quickActions: 'Aksyon Rapid',
    },
    // Course
    course: {
      lessons: 'leson',
      viewCourse: 'Gade Kou',
      backToCourses: 'Retounen nan Kou',
      courseLessons: 'Leson Kou',
      watch: 'Gade',
      progress: 'Pwogrè',
      completed: 'Fini',
      markComplete: 'Make Fini',
      notes: 'Nòt Leson',
      downloadPdf: 'Telechaje PDF',
    },
    // Legal
    legal: {
      terms: 'Kondisyon Sèvis',
      privacy: 'Politik Konfidansyalite',
      disclaimer: 'Avètisman',
      contact: 'Kontak',
    },
    // Common
    common: {
      loading: 'Chajman...',
      error: 'Erè',
      success: 'Siksè',
      save: 'Sove',
      cancel: 'Anile',
      delete: 'Efase',
      edit: 'Modifye',
      add: 'Ajoute',
      search: 'Chèche',
      filter: 'Filtre',
      all: 'Tout',
      active: 'Aktif',
      admin: 'Admin',
      student: 'Elèv',
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('cossy_language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('cossy_language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const value = {
    language,
    setLanguage,
    t,
    languages: [
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'ht', name: 'Kreyòl Ayisyen', flag: '🇭🇹' },
    ],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
